import { concat, fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import optional from '@nullvoxpopuli/ember-composable-helpers/helpers/optional';
import { element } from 'ember-element-helper';
import { and, not, or } from 'ember-truth-helpers';
import BvIcon from 'volta/components/bv-icon';
import BvTooltip from 'volta/components/bv-tooltip';
import cx from 'volta/helpers/class-names';
import colorVar from 'volta/helpers/color-var';
import stopPropagation from 'volta/helpers/stop-propagation';
import cssVars from 'volta/modifiers/css-vars';

import type { ContentValue } from '@glint/template';
import type { BvTooltipSignature } from 'volta/components/bv-tooltip';
import type { THueVariationString, TIntent, TSize } from 'volta/utils/style-utils';

type TTagSize = Extract<TSize, 'sm' | 'lg'>;
const SIZES: TTagSize[] = ['sm', 'lg'];

type IColor = TIntent | 'primary';
const COLORS: IColor[] = ['primary', 'success', 'danger', 'warning', 'info'];

export interface BvTagSignature {
  Args: {
    text?: ContentValue;
    size?: TTagSize;
    color?: THueVariationString;
    disabled?: boolean;
    fullWidth?: boolean;
    class?: string;
    textClass?: string;
    buttonLabel?: string;
    tooltip?: BvTooltipSignature['Args']['text'];
    onRemove?: (e: Event) => void;
    onClick?: (e: MouseEvent) => void;
  };
  Blocks: {
    default: [];
  };
  Element: HTMLElement;
}

export default class BvTagComponent extends Component<BvTagSignature> {
  get sizeClass() {
    const { size } = this.args;
    return size && SIZES.includes(size) ? `bv-tag--${size}` : undefined;
  }

  get colorClass() {
    const { color } = this.args;
    return color && COLORS.includes(color as IColor) && `bv-tag--${color}`;
  }

  @action
  handleMouseUp(e: MouseEvent) {
    const { currentTarget } = e;

    if (currentTarget && typeof (currentTarget as HTMLButtonElement)['blur'] === 'function') {
      (currentTarget as HTMLButtonElement).blur();
    }
  }

  <template>
    {{#let
      (element (if @onClick 'button' 'span'))
      (and (not this.colorClass) @color)
      (cx
        'bv-tag'
        (if @onClick 'bv-tag--clickable')
        (if @fullWidth 'bv-tag--block')
        (if @onRemove 'bv-tag--removable')
        (if @disabled 'bv-tag--disabled')
        this.sizeClass
        this.colorClass
        @class
      )
      as |HtmlElement customColor classNames|
    }}
      <HtmlElement
        class={{classNames}}
        type={{if @onClick 'button'}}
        ...attributes
        {{on 'click' (fn (optional @onClick))}}
        {{cssVars
          bv-tag-text=(if customColor (colorVar @color))
          bv-tag-color=(if customColor (colorVar @color alpha=0.1 rgba=true))
          bv-tag-shadow=(if customColor (colorVar @color alpha=0.2 rgba=true))
          bv-tag-shadow-hovered=(if customColor (colorVar @color alpha=0.3 rgba=true))
          bv-tag-remove-hovered=(if customColor (colorVar @color alpha=0.2 rgba=true))
        }}
      >
        <span
          data-test-tag-text
          title={{concat @text}}
          class={{cx 'bv-tag__text' @textClass (if @fullWidth 'u-flex-1')}}
        >
          {{#if (has-block)}}
            {{yield}}
          {{else}}
            {{@text}}
          {{/if}}
        </span>
        {{#if @onRemove}}
          {{! template-lint-disable no-pointer-down-event-binding }}
          <button
            data-test-tag-button
            type='button'
            class='bv-tag__remove'
            aria-label={{@buttonLabel}}
            disabled={{@disabled}}
            tabindex='-1'
            {{on 'mousedown' (stopPropagation)}}
            {{on 'click' (stopPropagation @onRemove)}}
            {{on 'mouseup' this.handleMouseUp}}
          >
            <BvIcon data-test-tag-button-icon={{true}} @glyph='close' @size={{or @size 'md'}} />
          </button>
        {{/if}}
        {{#if @tooltip}}
          <BvTooltip @text={{@tooltip}} />
        {{/if}}
      </HtmlElement>
    {{/let}}
  </template>
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    BvTag: typeof BvTagComponent;
    'bv-tag': typeof BvTagComponent;
  }
}
